import React, { useState, useEffect, useContext } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  PieChart, 
  Users, 
  DollarSign, 
  ShoppingBag, 
  Clock, 
  Calendar, 
  ArrowUpRight, 
  ArrowDownRight,
  ChevronLeft,
  Filter
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AppContext from '../../context/AppContext';

// Composant pour les mini-cartes statistiques
const StatCard = ({ title, value, percentage, isPositive, icon }) => (
  <div className="bg-white rounded-2xl p-4 shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
    <div className="flex items-center gap-3 mb-2">
      <div className={`p-2 ${isPositive ? 'bg-green-100' : 'bg-blue-100'} rounded-lg`}>
        {icon}
      </div>
      <p className="text-sm font-medium text-gray-500">{title}</p>
    </div>
    <div className="flex items-baseline justify-between">
      <p className="text-2xl font-bold text-gray-800">{value}</p>
      {percentage && (
        <div className={`flex items-center ${isPositive ? 'text-green-600' : 'text-blue-600'}`}>
          <span className="text-xs font-medium">{percentage}%</span>
          {isPositive ? 
            <ArrowUpRight className="w-3 h-3 ml-1" /> : 
            <ArrowDownRight className="w-3 h-3 ml-1" />
          }
        </div>
      )}
    </div>
  </div>
);

// Composant pour le graphique à barres (simulation)
const BarChartComponent = ({ data }) => (
  <div className="bg-white rounded-2xl p-6 shadow-md">
    <div className="flex justify-between items-center mb-6">
      <h3 className="text-lg font-semibold text-gray-800">Ventes par Jour</h3>
      <div className="flex gap-2">
        <button className="bg-gray-100 text-gray-700 px-3 py-1 rounded-lg text-xs font-medium hover:bg-gray-200 transition-colors">
          7J
        </button>
        <button className="bg-blue-600 text-white px-3 py-1 rounded-lg text-xs font-medium">
          30J
        </button>
        <button className="bg-gray-100 text-gray-700 px-3 py-1 rounded-lg text-xs font-medium hover:bg-gray-200 transition-colors">
          Année
        </button>
      </div>
    </div>
    
    <div className="h-64 flex items-end gap-1 justify-between pb-6 pt-2">
      {data.map((item, index) => (
        <div key={index} className="flex flex-col items-center gap-2">
          <div 
            className={`w-8 rounded-t-lg ${item.highlight ? 'bg-blue-600' : 'bg-blue-100'}`} 
            style={{height: `${item.value * 2}px`}}
          ></div>
          <span className="text-xs text-gray-500">{item.label}</span>
        </div>
      ))}
    </div>
  </div>
);

// Composant pour le tableau des dernières transactions
const TransactionRow = ({ id, customer, date, amount, status }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'failed': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };
  
  return (
    <tr className="hover:bg-gray-50 transition-colors">
      <td className="py-3 pl-6 pr-3">
        <span className="font-medium text-sm">#{id}</span>
      </td>
      <td className="py-3 px-3">
        <span className="text-sm">{customer}</span>
      </td>
      <td className="py-3 px-3">
        <span className="text-sm text-gray-500">{date}</span>
      </td>
      <td className="py-3 px-3">
        <span className="text-sm font-medium">{amount} FCFA</span>
      </td>
      <td className="py-3 px-3">
        <span className={`inline-flex px-2 py-1 rounded-full text-xs ${getStatusColor(status)}`}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      </td>
    </tr>
  );
};

// Composant pour les analytiques des utilisateurs (top clients)
const UserAnalytics = ({ users }) => (
  <div className="bg-white rounded-2xl p-6 shadow-md">
    <div className="flex justify-between items-center mb-6">
      <h3 className="text-lg font-semibold text-gray-800">Top Clients</h3>
      <button className="text-blue-600 text-sm font-medium flex items-center gap-1">
        Voir tous <ArrowUpRight className="w-3 h-3" />
      </button>
    </div>
    
    <div className="space-y-4">
      {users.map((user, index) => (
        <div key={index} className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-medium">
              {user.name.charAt(0)}
            </div>
            <div>
              <p className="font-medium text-gray-800">{user.name}</p>
              <p className="text-xs text-gray-500">{user.purchases} achats</p>
            </div>
          </div>
          <p className="font-bold text-gray-800">{user.spent} FCFA</p>
        </div>
      ))}
    </div>
  </div>
);

// Graphique circulaire simplifié
const PieChartComponent = ({ data }) => (
  <div className="bg-white rounded-2xl p-6 shadow-md">
    <div className="flex justify-between items-center mb-6">
      <h3 className="text-lg font-semibold text-gray-800">Ventes par Produit</h3>
      <button className="text-blue-600 text-sm font-medium flex items-center gap-1">
        Détails <ArrowUpRight className="w-3 h-3" />
      </button>
    </div>
    
    <div className="flex items-center justify-center relative">
      <div className="w-32 h-32 rounded-full border-8 border-l-blue-500 border-t-purple-500 border-r-green-500 border-b-orange-400 transform rotate-45"></div>
      <div className="absolute inset-0 flex items-center justify-center">
        <p className="text-lg font-bold text-gray-800">100%</p>
      </div>
    </div>
    
    <div className="grid grid-cols-2 gap-4 mt-6">
      {data.map((item, index) => (
        <div key={index} className="flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${item.color}`}></div>
          <div>
            <p className="text-xs font-medium text-gray-800">{item.label}</p>
            <p className="text-xs text-gray-500">{item.value}%</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const Statistics = () => {
  const navigate = useNavigate();
  const { userInfos } = useContext(AppContext);
  const [timeFilter, setTimeFilter] = useState('week');

  // Données fictives pour les statistiques
  const statCards = [
    {
      title: "Total des Revenus",
      value: "538,500 FCFA",
      percentage: "12.3",
      isPositive: true,
      icon: <DollarSign className="w-4 h-4 text-green-500" />
    },
    {
      title: "Nombre de Clients",
      value: "182",
      percentage: "4.7",
      isPositive: true,
      icon: <Users className="w-4 h-4 text-blue-500" />
    },
    {
      title: "Produits Vendus",
      value: "463",
      percentage: "1.2",
      isPositive: false,
      icon: <ShoppingBag className="w-4 h-4 text-purple-500" />
    },
    {
      title: "Taux de Conversion",
      value: "6.4%",
      percentage: "2.8",
      isPositive: true,
      icon: <TrendingUp className="w-4 h-4 text-orange-500" />
    }
  ];

  // Données fictives pour le graphique à barres
  const barChartData = [
    { label: 'Lun', value: 25 },
    { label: 'Mar', value: 40 },
    { label: 'Mer', value: 30 },
    { label: 'Jeu', value: 22 },
    { label: 'Ven', value: 55, highlight: true },
    { label: 'Sam', value: 48 },
    { label: 'Dim', value: 32 }
  ];

  // Données fictives pour les transactions
  const transactions = [
    { id: '8721', customer: 'Sophie Martin', date: '12 Mar 2025', amount: '12,500', status: 'completed' },
    { id: '8720', customer: 'Thomas Dubois', date: '11 Mar 2025', amount: '8,350', status: 'pending' },
    { id: '8719', customer: 'Emma Leclerc', date: '10 Mar 2025', amount: '15,000', status: 'completed' },
    { id: '8718', customer: 'Hugo Bernard', date: '09 Mar 2025', amount: '5,200', status: 'failed' },
    { id: '8717', customer: 'Camille Petit', date: '08 Mar 2025', amount: '9,750', status: 'completed' }
  ];

  // Données fictives pour les top utilisateurs
  const topUsers = [
    { name: 'Emma Leclerc', purchases: 12, spent: '87,500' },
    { name: 'Thomas Dubois', purchases: 8, spent: '56,200' },
    { name: 'Sophie Martin', purchases: 7, spent: '42,800' }
  ];

  // Données fictives pour le diagramme circulaire
  const pieChartData = [
    { label: 'Formation Pro', value: 42, color: 'bg-blue-500' },
    { label: 'Consultation', value: 28, color: 'bg-purple-500' },
    { label: 'Ebooks', value: 18, color: 'bg-green-500' },
    { label: 'Autres', value: 12, color: 'bg-orange-400' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Contenu principal */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Filtres de période */}
        <div className="bg-white rounded-2xl shadow-md p-4 mb-6 flex items-center justify-between">
          <div className="flex gap-2">
            <button 
              onClick={() => setTimeFilter('week')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                timeFilter === 'week' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Cette semaine
            </button>
            <button 
              onClick={() => setTimeFilter('month')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                timeFilter === 'month' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Ce mois
            </button>
            <button 
              onClick={() => setTimeFilter('year')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                timeFilter === 'year' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Cette année
            </button>
          </div>
          
          <button className="text-blue-600 text-sm font-medium">
            Personnaliser
          </button>
        </div>
        
        {/* Cartes statistiques */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          {statCards.map((stat, index) => (
            <StatCard key={index} {...stat} />
          ))}
        </div>
        
        {/* Graphique à barres */}
        <div className="mb-6">
          <BarChartComponent data={barChartData} />
        </div>
        
        {/* Dernières transactions */}
        <div className="bg-white rounded-2xl shadow-md overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-800">Dernières Transactions</h3>
            <button className="text-blue-600 text-sm font-medium flex items-center gap-1">
              Voir toutes <ArrowUpRight className="w-3 h-3" />
            </button>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="py-3 pl-6 pr-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="py-3 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                  <th className="py-3 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="py-3 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Montant</th>
                  <th className="py-3 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {transactions.map((transaction, index) => (
                  <TransactionRow key={index} {...transaction} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Grille de graphiques inférieurs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <PieChartComponent data={pieChartData} />
          <UserAnalytics users={topUsers} />
        </div>
      </div>
    </div>
  );
};

export default Statistics;